import { useState } from "react";
import { Link } from "react-router";
import { cartStore } from "../lib/cart.ts";
import { money, sortOptionNames, sortValues } from "../lib/format.ts";
import { toast } from "../lib/toast.ts";
import type { Product } from "../lib/types.ts";
import { flyToBag } from "./fly.ts";
import { Icon } from "./Icon.tsx";
import { Sticker } from "./Sticker.tsx";

const FACES = ["var(--sky)", "var(--yolk)", "var(--tomato)"];

export function addProductVariant(
  p: Product,
  variantId: string,
  source: Element | null,
  currency: string,
) {
  const v = p.variants.find((x) => x.id === variantId);
  if (!v) return;
  cartStore.add({
    line: { kind: "catalog", variantId: v.id, quantity: 1 },
    title: p.title,
    variantTitle: v.title,
    imageUrl: p.images[0] ?? null,
    unitPriceCents: v.priceCents,
    href: `/p/${p.slug}`,
  });
  flyToBag(source, p.images[0] ?? null);
  toast(`${p.title} (${v.title}) is in your bag`, { action: { label: "View bag", href: "/bag" } });
  void currency;
}

/** In-sticker variant picker: choose colour/size without leaving the sheet. */
function QuickAdd({
  product,
  onDone,
  currency,
}: {
  product: Product;
  onDone: () => void;
  currency: string;
}) {
  const names = sortOptionNames(product.options.map((o) => o.name));
  const [picked, setPicked] = useState<Record<string, string>>({});
  const match = product.variants.find(
    (v) => v.available && names.every((n) => picked[n] && v.options[n] === picked[n]),
  );
  return (
    <div className="quickadd" role="dialog" aria-label={`Choose options for ${product.title}`}>
      {names.map((name) => {
        const opt = product.options.find((o) => o.name === name)!;
        return (
          <fieldset key={name} className="quickadd__group">
            <legend>{name}</legend>
            <div className="chips">
              {sortValues(name, opt.values).map((val) => {
                const possible = product.variants.some(
                  (v) =>
                    v.available &&
                    v.options[name] === val &&
                    names.every((n) => n === name || !picked[n] || v.options[n] === picked[n]),
                );
                return (
                  <button
                    type="button"
                    key={val}
                    className="chip"
                    aria-pressed={picked[name] === val}
                    disabled={!possible}
                    onClick={() => setPicked((s) => ({ ...s, [name]: val }))}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      })}
      <div className="quickadd__actions">
        <button type="button" className="link-btn" onClick={onDone}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--sm"
          disabled={!match}
          onClick={(e) => {
            if (!match) return;
            addProductVariant(product, match.id, e.currentTarget.closest(".pstick__art"), currency);
            onDone();
          }}
        >
          Add {match ? money(match.priceCents, currency) : ""}
        </button>
      </div>
    </div>
  );
}

export function ProductSticker({
  product,
  index,
  currency,
  shape = "rect",
  tilt,
}: {
  product: Product;
  index: number;
  currency: string;
  shape?: "rect" | "round";
  tilt?: number;
}) {
  const [picking, setPicking] = useState(false);
  const single = product.variants.filter((v) => v.available);
  return (
    <article className="pstick" data-shape={shape}>
      <div className="pstick__art">
        <Sticker
          face={FACES[index % FACES.length]}
          tilt={tilt ?? (index % 2 === 0 ? -2 : 2.5)}
          shape={shape}
          peelable={shape === "rect"}
        >
          {/* The meme itself, big: it's what people are buying. Photos only when there's no flat art. */}
          {(product.artworkUrl ?? product.images[0]) ? (
            <img
              className="pstick__img"
              src={product.artworkUrl ?? product.images[0]}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : null}
          <Link to={`/p/${product.slug}`} className="pstick__link" tabIndex={picking ? -1 : 0}>
            <span className="visually-hidden">{product.title}</span>
          </Link>
        </Sticker>
        {/* Sits on the die-cut edge, like the price tag, so it never covers the meme. */}
        {!picking && (
          <button
            type="button"
            className="pstick__add"
            aria-label={`Add ${product.title} to bag`}
            aria-haspopup="dialog"
            onClick={(e) => {
              if (single.length === 1)
                addProductVariant(
                  product,
                  single[0]!.id,
                  e.currentTarget.closest(".pstick__art"),
                  currency,
                );
              else setPicking(true);
            }}
          >
            <Icon name="plus" size={22} />
          </button>
        )}
        <span className="tag pstick__price">{money(product.priceCents, currency)}</span>
        {picking && (
          <QuickAdd product={product} currency={currency} onDone={() => setPicking(false)} />
        )}
      </div>
      <h3 className="pstick__title">
        <Link to={`/p/${product.slug}`}>{product.title}</Link>
      </h3>
    </article>
  );
}

/** The empty slot on the sheet: an invitation to cut a new sticker. */
export function MakeSlot() {
  return (
    <Link to="/make" className="makeslot">
      <span className="makeslot__plus">
        <Icon name="plus" size={30} />
      </span>
      <span className="makeslot__title">Your meme goes here</span>
      <span className="makeslot__sub">Write a caption, put it on a tee.</span>
    </Link>
  );
}
